import React from 'react';
import { calculateTimelineHeight } from './utils/zoomHelpers';
import { calculateTimelineState, getTimelineSegmentColor, isTimeSlotPast, isToday } from './utils/timelineCalculator';

interface TimelineProps {
  containerHeight?: number;
  startHour?: number;
  endHour?: number;
  zoomLevel: number[];
  zoomIndex: number;
  isMobileOrTablet?: boolean;
  presentationDate?: string;
  presentationDay?: number;
}

const Timeline: React.FC<TimelineProps> = ({
  containerHeight = 1080,
  startHour = 1,
  endHour = 24,
  zoomLevel,
  zoomIndex,
  isMobileOrTablet,
  presentationDate,
  presentationDay
}) => {
  const dynamicHeight = calculateTimelineHeight(zoomLevel, zoomIndex);
  const currentDate = new Date();

  const timeSlots = Array.from(
    { length: endHour - startHour + 1 },
    (_, i) => `${startHour + i}h`
  );

  const hourHeight = dynamicHeight / timeSlots.length;

  // Get current time in hours for color calculation
  const currentHour = currentDate.getHours();
  const currentMinutes = currentDate.getMinutes();
  const currentTimeInHours = currentHour + (currentMinutes / 60);

  // Check if today is the same as presentation date
  const isCurrentDay = presentationDate ? isToday(presentationDate, currentDate) : false;

  // Calculate timeline state
  let timelineState = undefined;
  let timelineBarPosition = 0;
  let showTimelineBar = false;
  if (presentationDate && typeof presentationDay !== 'undefined') {
    timelineState = calculateTimelineState({
      currentDate,
      presentationDate,
      presentationDay,
      startHour,
      endHour,
      containerHeight: dynamicHeight
    });
    // --- Ensure bar position matches color logic ---
    if (isCurrentDay) {
      if (currentTimeInHours >= startHour && currentTimeInHours <= endHour) {
        let barIndex = Math.floor(currentTimeInHours - startHour);
        if (barIndex < 0) barIndex = 0;
        if (barIndex >= timeSlots.length) barIndex = timeSlots.length - 1;
        timelineBarPosition = (barIndex + 1) * hourHeight;
        showTimelineBar = true;
      }
    }
  }

  return (
    <div
      className="tw-sticky tw-left-0 tw-top-0 tw-z-40"
      style={{
        height: `${dynamicHeight}px`,
        marginBottom: isMobileOrTablet ? '1.375rem' : "0px"
      }}
    >
      <div className="tw-pl-0 mid-max:tw-pl-0 sm:tw-pl-8 lg:tw-pt-5 tw-relative">
        <div
          className="tw-relative tw-flex tw-flex-col sm:tw-rounded-full tw-rounded-none tw-h-full tw-z-50"
          style={{
            width: '1.875rem',
            backgroundColor: '#4B4F5A',
          }}
        >
          {timeSlots.map((slot, index) => {
            let backgroundColor = '#4B4F5A';
            let textColor = '#D7D8D9'; // Default text color

            if (presentationDate && isCurrentDay) {
              const isSlotPast = isTimeSlotPast(slot, presentationDate, currentDate);
              backgroundColor = isSlotPast ? '#32363F' : '#4B4F5A';
              textColor = isSlotPast ? '#53565e' : '#D7D8D9'; 
            } else if (presentationDate) {
              backgroundColor = getTimelineSegmentColor(
                index,
                currentTimeInHours,
                startHour,
                presentationDate,
                currentDate
              );

              if (backgroundColor === '#32363F') {
                textColor = '#53565e';
              } else {
                textColor = '#D7D8D9';
              }
            }

            return (
              <div
                key={index}
                className="tw-min-w-[80px] tw-p-2 tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-sm tw-relative"
                style={{
                  height: `${hourHeight}px`,
                  fontWeight: 700,
                  fontSize: '0.75em',
                  backgroundColor,
                  color: textColor,
                  borderRadius: !isMobileOrTablet && index === 0 ? '1rem 1rem 0 0' : !isMobileOrTablet && index === timeSlots.length - 1 ? '0 0 1rem 1rem' : '0',
                }}
              >
                {slot}
              </div>
            );
          })}

          {/* Timeline Bar - only show if it's today and within timeline range */}
          {isCurrentDay && showTimelineBar && (
            <div
              className="tw-absolute tw-left-0 tw-right-0 tw-z-50"
              style={{
                height: '3px',
                top: `${timelineBarPosition}px`,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                background: '#1080B2',
                width: '3.09rem',
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Timeline;